import {
  Article,
  ExternalLink,
  LeadLabel,
  List,
  ListItem,
  Page,
  Paragraph,
  SectionTitle,
  Title,
} from './styles';

const WEBSITE_TERMS_URL = 'https://gbsbenefits.com/about-us/terms-conditions/';

export const TermsDocument = (): JSX.Element => {
  return (
    <Page data-cy="terms-document-page">
      <Article data-cy="terms-document-article">
        <Title data-cy="terms-document-title">
          EDGE Platform Terms &amp; Conditions Addendum
        </Title>

        <SectionTitle>
          1. Applicability and Relationship to Existing Terms
        </SectionTitle>
        <Paragraph>
          This EDGE Platform Terms &amp; Conditions Addendum (&ldquo;EDGE
          Terms&rdquo;) supplements and incorporates by reference the existing
          GBS Benefits, Inc. Terms &amp; Conditions available at:{' '}
          <ExternalLink
            href={WEBSITE_TERMS_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-cy="terms-document-website-terms-link"
          >
            {WEBSITE_TERMS_URL}
          </ExternalLink>
        </Paragraph>
        <Paragraph>
          In the event of a conflict between these EDGE Terms and the Website
          Terms, these EDGE Terms shall control solely with respect to the EDGE
          platform. All other provisions of the Website Terms remain in full
          force and effect.
        </Paragraph>

        <SectionTitle>2. EDGE Platform Overview</SectionTitle>
        <Paragraph>
          The EDGE platform (&ldquo;EDGE&rdquo;) is a secure, invitation-only,
          single sign-on (SSO) web-based platform that enables authorized users
          to access, view, and manage employee benefits-related information.
        </Paragraph>
        <Paragraph>
          EDGE is not a system of record and functions solely as a data
          aggregation and presentation layer. All underlying data originates
          from third-party systems.
        </Paragraph>
        <Paragraph>Access to EDGE is limited to:</Paragraph>
        <List>
          <ListItem>Employees of GBS Benefits, Inc. or its affiliates</ListItem>
          <ListItem>Employer clients (&ldquo;Clients&rdquo;)</ListItem>
          <ListItem>Employees of Clients (&ldquo;End Users&rdquo;)</ListItem>
        </List>
        <Paragraph>
          Access is granted only through authorized provisioning and
          authentication mechanisms. Public registration is not permitted.
        </Paragraph>

        <SectionTitle>3. User Roles and Responsibilities</SectionTitle>
        <Paragraph>
          Access to EDGE and available functionality is role-based and
          determined by user type (e.g., administrator, employer, employee).
        </Paragraph>
        <Paragraph>By using EDGE, you agree to:</Paragraph>
        <List>
          <ListItem>
            Maintain the confidentiality of your login credentials
          </ListItem>
          <ListItem>
            Accept responsibility for all activities conducted under your
            account
          </ListItem>
          <ListItem>
            Promptly notify GBS Benefits of any unauthorized access or suspected
            security breach
          </ListItem>
        </List>
        <Paragraph>
          Your access to EDGE is limited, revocable, non-transferable, and
          subject to compliance with these EDGE Terms and the Website Terms.
        </Paragraph>

        <SectionTitle>4. Data Types and Use</SectionTitle>
        <Paragraph>
          EDGE may display and process certain categories of data, including:
        </Paragraph>
        <Paragraph>
          <LeadLabel>Personally Identifiable Information (PII):</LeadLabel>
        </Paragraph>
        <List>
          <ListItem>Name, address, email, phone number, date of birth</ListItem>
          <ListItem>Employer and employment-related information</ListItem>
        </List>
        <Paragraph>
          <LeadLabel>Benefits Information:</LeadLabel>
        </Paragraph>
        <List>
          <ListItem>Plan enrollment data</ListItem>
          <ListItem>Dependent information</ListItem>
          <ListItem>Benefits summaries and identification cards</ListItem>
        </List>
        <Paragraph>
          <LeadLabel>Protected Health Information (PHI):</LeadLabel>
        </Paragraph>
        <List>
          <ListItem>
            Limited health-related benefits information, where applicable
          </ListItem>
        </List>
        <Paragraph>EDGE does not collect, store, or process:</Paragraph>
        <List>
          <ListItem>Social Security Numbers (SSNs)</ListItem>
          <ListItem>Payment or financial account information</ListItem>
          <ListItem>Payroll or compensation data</ListItem>
        </List>

        <SectionTitle>
          5. HIPAA Compliance and Business Associate Relationship
        </SectionTitle>
        <Paragraph>
          To the extent EDGE involves the use or disclosure of Protected Health
          Information (&ldquo;PHI&rdquo;), GBS Benefits acts as a Business
          Associate (as defined under HIPAA) pursuant to applicable Business
          Associate Agreements (&ldquo;BAAs&rdquo;) with Clients.
        </Paragraph>
        <Paragraph>GBS Benefits agrees to:</Paragraph>
        <List>
          <ListItem>
            Implement appropriate administrative, physical, and technical
            safeguards to protect PHI
          </ListItem>
          <ListItem>
            Use and disclose PHI only as permitted by applicable BAAs and law
          </ListItem>
          <ListItem>
            Report known breaches of unsecured PHI in accordance with applicable
            law
          </ListItem>
          <ListItem>
            Ensure that subcontractors who access PHI agree to similar
            restrictions and safeguards
          </ListItem>
        </List>
        <Paragraph>Clients remain responsible for:</Paragraph>
        <List>
          <ListItem>
            Determining permissible uses and disclosures of PHI
          </ListItem>
          <ListItem>
            Ensuring appropriate authorization and consent from End Users
          </ListItem>
          <ListItem>
            Compliance with applicable privacy laws as Covered Entities or Plan
            Sponsors
          </ListItem>
        </List>

        <SectionTitle>6. Data Ownership and Accuracy</SectionTitle>
        <Paragraph>
          All data accessible through EDGE is owned and controlled by the
          applicable Client.
        </Paragraph>
        <Paragraph>You acknowledge and agree that:</Paragraph>
        <List>
          <ListItem>
            GBS Benefits does not originate or control the underlying data
          </ListItem>
          <ListItem>
            Data is sourced from third-party systems, including insurance
            carriers and HRIS platforms
          </ListItem>
          <ListItem>EDGE is not the system of record</ListItem>
          <ListItem>
            GBS Benefits makes no representations or warranties regarding the
            accuracy, completeness, or timeliness of any data displayed within
            EDGE.
          </ListItem>
        </List>

        <SectionTitle>7. Third-Party Data and Integrations</SectionTitle>
        <Paragraph>
          EDGE may rely on integrations with third-party systems, including
          insurance carriers and HRIS platforms.
        </Paragraph>
        <Paragraph>GBS Benefits:</Paragraph>
        <List>
          <ListItem>Does not control such third-party systems</ListItem>
          <ListItem>
            Is not responsible for data accuracy, availability, or security
            within those systems
          </ListItem>
          <ListItem>
            Disclaims all liability arising from third-party data errors,
            delays, or system failures
          </ListItem>
        </List>

        <SectionTitle>8. Security Measures</SectionTitle>
        <Paragraph>
          GBS Benefits implements reasonable and appropriate security measures
          designed to protect data within EDGE, including:
        </Paragraph>
        <List>
          <ListItem>Encryption of data in transit</ListItem>
          <ListItem>Security monitoring and vulnerability management</ListItem>
          <ListItem>Access controls and authentication protocols</ListItem>
        </List>
        <Paragraph>However:</Paragraph>
        <List>
          <ListItem>
            Multi-factor authentication (MFA) may not be enabled
          </ListItem>
          <ListItem>
            No system can be guaranteed to be completely secure
          </ListItem>
          <ListItem>
            Users are responsible for maintaining the security of their
            credentials and devices.
          </ListItem>
        </List>

        <SectionTitle>9. Permitted Use and Restrictions</SectionTitle>
        <Paragraph>You agree not to:</Paragraph>
        <List>
          <ListItem>
            Access or attempt to access data beyond your authorized role
          </ListItem>
          <ListItem>Use EDGE for unlawful or unauthorized purposes</ListItem>
          <ListItem>
            Reverse engineer, scrape, or extract data using automated tools
          </ListItem>
          <ListItem>
            Upload or transmit unauthorized or non-compliant data
          </ListItem>
        </List>

        <SectionTitle>10. Document Uploads</SectionTitle>
        <Paragraph>
          Authorized Client users may upload documents to EDGE.
        </Paragraph>
        <Paragraph>
          By uploading content, you represent and warrant that:
        </Paragraph>
        <List>
          <ListItem>You have the legal right to share such content</ListItem>
          <ListItem>
            The content complies with all applicable laws and regulations,
            including privacy laws
          </ListItem>
          <ListItem>
            The content does not infringe upon third-party rights
          </ListItem>
        </List>
        <Paragraph>
          GBS Benefits reserves the right, but not the obligation, to remove
          content that violates these terms.
        </Paragraph>

        <SectionTitle>11. No System of Record / No Reliance</SectionTitle>
        <Paragraph>
          EDGE is provided for informational and convenience purposes only.
        </Paragraph>
        <Paragraph>Users agree:</Paragraph>
        <List>
          <ListItem>
            Not to rely on EDGE as the authoritative source for benefits
            elections or coverage
          </ListItem>
          <ListItem>
            To verify all information with the applicable system of record,
            employer, or insurance carrier
          </ListItem>
        </List>

        <SectionTitle>12. Disclaimer of Warranties</SectionTitle>
        <Paragraph>
          EDGE is provided on an &ldquo;as is&rdquo; and &ldquo;as
          available&rdquo; basis.
        </Paragraph>
        <Paragraph>
          GBS Benefits disclaims all warranties, express or implied, including
          but not limited to:
        </Paragraph>
        <List>
          <ListItem>Accuracy or completeness of data</ListItem>
          <ListItem>Continuous availability or uninterrupted access</ListItem>
          <ListItem>Fitness for a particular purpose</ListItem>
        </List>
        <Paragraph>
          Data displayed in EDGE may be delayed, incomplete, or inaccurate due
          to third-party dependencies.
        </Paragraph>

        <SectionTitle>13. Limitation of Liability</SectionTitle>
        <Paragraph>
          To the fullest extent permitted by law, GBS Benefits shall not be
          liable for:
        </Paragraph>
        <List>
          <ListItem>Decisions made based on data displayed in EDGE</ListItem>
          <ListItem>Errors or inaccuracies in third-party data</ListItem>
          <ListItem>
            Unauthorized access resulting from user actions or credential
            compromise
          </ListItem>
          <ListItem>System downtime, interruptions, or delays</ListItem>
        </List>
        <Paragraph>
          This limitation includes any claims related to PII or PHI, except to
          the extent liability cannot be limited under applicable law.
        </Paragraph>

        <SectionTitle>14. Availability and Modifications</SectionTitle>
        <Paragraph>
          GBS Benefits does not guarantee uptime, availability, or performance
          of EDGE.
        </Paragraph>
        <Paragraph>GBS Benefits reserves the right to:</Paragraph>
        <List>
          <ListItem>Modify, suspend, or discontinue EDGE at any time</ListItem>
          <ListItem>Update these EDGE Terms at its discretion</ListItem>
        </List>
        <Paragraph>
          Continued use of EDGE constitutes acceptance of any changes.
        </Paragraph>

        <SectionTitle>15. Governing Law and Dispute Resolution</SectionTitle>
        <Paragraph>
          These EDGE Terms are governed by the laws of the State of Utah.
        </Paragraph>
        <Paragraph>
          All disputes shall be subject to the arbitration provisions set forth
          in the Website Terms available at:{' '}
          <ExternalLink
            href={WEBSITE_TERMS_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-cy="terms-document-website-terms-link-section-15"
          >
            {WEBSITE_TERMS_URL}
          </ExternalLink>
        </Paragraph>

        <SectionTitle>16. Acknowledgment</SectionTitle>
        <Paragraph>
          By accessing or using EDGE, you acknowledge that you have read,
          understood, and agree to these EDGE Terms and the Website Terms.
        </Paragraph>
      </Article>
    </Page>
  );
};
